'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { Link as RACLink } from 'react-aria-components';
import { IconPushLeft, IconPushRight } from '../../icons/index.js';
import { stringLikeChildrenToString } from '../../utils/string-like-children-to-string.js';
import { useIsMobile } from '../../utils/use-is-mobile.js';
import { IconButton } from '../button/icon-button.js';
import { Drawer, DrawerBody } from '../drawer/drawer.js';
import { UNSTABLE_Hidden } from '../hidden/hidden.js';
import { Tooltip } from '../tooltip/tooltip.js';
import type { Href, StringLikeChildren } from '../types.js';
import { Text } from '../typography/text.js';

const SideNavContext = createContext<{ isOpen: boolean }>({ isOpen: false });

export interface UNSTABLE_SideNavProps {
  children: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Determines how the side nav is displayed and behaves.
   * - `overlay`: The side nav slides in as an overlay on top of the main content. This is used when all available screen space is needed for the main content.
   * - `fixed`: The side nav is fixed to the side and includes an expand button to toggle between a compact icon-only view and an expanded view showing labels. On smaller screens, it slides in as an overlay.
   *
   * @default 'fixed'
   */
  variant?: 'overlay' | 'fixed';
}

export function UNSTABLE_SideNav({ children, isOpen, onOpenChange, variant = 'fixed' }: UNSTABLE_SideNavProps) {
  const isMobile = useIsMobile();

  const drawer = (isMobile || variant === 'overlay') && (
    <Drawer UNSTABLE_placement="start" isOpen={isOpen} onOpenChange={onOpenChange} aria-label="Side navigation">
      <DrawerBody>{children}</DrawerBody>
    </Drawer>
  );

  return (
    <SideNavContext.Provider value={{ isOpen: isOpen ?? false }}>
      {drawer}
      {variant === 'fixed' && (
        <UNSTABLE_Hidden hide={{ sm: true, md: false }}>
          <div className="cim-side-nav" data-expanded={isOpen}>
            <div className="cim-side-nav-header">
              <IconButton
                variant="tertiary"
                icon={isOpen ? <IconPushLeft /> : <IconPushRight />}
                aria-label="Expand sidebar"
                onPress={() => onOpenChange?.(!isOpen)}
              />
            </div>
            <div className="cim-side-nav-list-container">{children}</div>
          </div>
        </UNSTABLE_Hidden>
      )}
    </SideNavContext.Provider>
  );
}

export interface UNSTABLE_SideNavListProps {
  children: ReactNode;
}

export function UNSTABLE_SideNavList({ children }: UNSTABLE_SideNavListProps) {
  return <div className="cim-side-nav-list">{children}</div>;
}

export interface UNSTABLE_SideNavListItemProps {
  children: StringLikeChildren;
  href: Href;
  isActive?: boolean;
  icon?: ReactNode;
}

export function UNSTABLE_SideNavListItem({ children, href, isActive, icon }: UNSTABLE_SideNavListItemProps) {
  const { isOpen } = useContext(SideNavContext);

  return (
    <Tooltip label={stringLikeChildrenToString(children)} placement="right" isDisabled={isOpen}>
      <RACLink href={href} className="cim-side-nav-list-item" data-active={isActive} data-expanded={isOpen}>
        {icon}
        <Text as="span" variant="medium" UNSAFE_className="cim-side-nav-list-item-label">
          {children}
        </Text>
      </RACLink>
    </Tooltip>
  );
}

export interface UNSTABLE_SideNavListSectionProps {
  children: ReactNode;
  label?: string;
}

export function UNSTABLE_SideNavListSection({ children, label }: UNSTABLE_SideNavListSectionProps) {
  const { isOpen } = useContext(SideNavContext);

  return (
    <div className="cim-side-nav-list-section" data-expanded={isOpen}>
      {label && (
        <div className="cim-side-nav-list-section-header">
          <Text as="p" variant="small-semibold" tone="subtle" UNSAFE_className="cim-side-nav-list-section-header-label">
            {label}
          </Text>
        </div>
      )}
      {children}
    </div>
  );
}
